/**
 * Admin Lessons Page
 *
 * Page for managing lessons per level (Director only).
 * Lets the director add/edit/delete lessons (e.g. add a real Lesson alongside
 * the Introduction so the first month has two files), attaching a Drive link.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminLessons,
  useAdminLevels,
  useClassLessonPlan,
  useCreateLesson,
  useCustomizeClassLessonPlan,
  useDeleteLesson,
  useReorderLessons,
  useResetClassLessonPlan,
  useSetClassLessonPlan,
  useUpdateLesson,
} from "../../hooks/useAdmin";
import type { AdminLesson, CreateLessonRequest } from "../../types/admin";

// Calendar month for each lesson (1-12). The lesson becomes visible to
// students from this month onward (absolute calendar, not tied to intake).
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const emptyForm: CreateLessonRequest = {
  levelCode: "",
  topic: "",
  subtitle: "",
  description: "",
  order: 1,
  monthAvailable: 1,
  durationMinutes: 60,
  status: "published",
  slidesUrl: "",
  filesUrl: "",
  playlistUrl: "",
};

export function AdminLessonsPage() {
  const { data: levels } = useAdminLevels();
  const [levelFilter, setLevelFilter] = useState("");
  // When a specific class is selected, we edit that class's lesson plan instead
  // of the level's shared lessons.
  const [classFilter, setClassFilter] = useState("");
  const { data: classes } = useAdminClassGroups(
    levelFilter ? { level: levelFilter } : undefined,
  );
  const selectedClass = (classes ?? []).find((c) => c.id === classFilter);
  const { data: lessons, isLoading } = useAdminLessons({
    level: levelFilter || undefined,
  });
  const createLesson = useCreateLesson();
  const updateLesson = useUpdateLesson();
  const deleteLesson = useDeleteLesson();
  const reorderLessons = useReorderLessons();

  // Drag-and-drop reordering (only when a single level is selected).
  const canReorder = !!levelFilter;
  const [orderedLessons, setOrderedLessons] = useState<AdminLesson[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  useEffect(() => {
    setOrderedLessons(lessons ?? []);
  }, [lessons]);

  const handleDrop = (toIndex: number) => {
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...orderedLessons];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setOrderedLessons(next);
    setDragIndex(null);
    reorderLessons.mutate({
      level: levelFilter,
      items: next.map((l, i) => ({ id: l.id, order: i })),
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<AdminLesson | null>(null);
  const [form, setForm] = useState<CreateLessonRequest>(emptyForm);
  const [toDelete, setToDelete] = useState<AdminLesson | null>(null);

  const openCreate = () => {
    setForm({
      ...emptyForm,
      levelCode: levelFilter || levels?.[0]?.code || "",
    });
    setIsEditing(false);
    setSelected(null);
    setIsModalOpen(true);
  };

  const openEdit = (lesson: AdminLesson) => {
    setForm({
      levelCode: lesson.levelCode,
      topic: lesson.topic,
      subtitle: lesson.subtitle,
      description: lesson.description,
      order: lesson.order,
      monthAvailable: lesson.monthAvailable,
      durationMinutes: lesson.durationMinutes,
      status: lesson.status,
      slidesUrl: lesson.slidesUrl ?? "",
      filesUrl: lesson.filesUrl ?? "",
      playlistUrl: lesson.playlistUrl ?? "",
    });
    setSelected(lesson);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelected(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && selected) {
      await updateLesson.mutateAsync({ id: selected.id, data: form });
    } else {
      await createLesson.mutateAsync(form);
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    await deleteLesson.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Manage Lessons
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Add and edit lessons per level (attach a Drive link as Files)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={levelFilter}
                onChange={(e) => {
                  setLevelFilter(e.target.value);
                  setClassFilter("");
                }}
                className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              >
                <option value="">All levels</option>
                {levels?.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.code}
                  </option>
                ))}
              </select>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                title="Pick a class to customize its lessons"
              >
                <option value="">Level default (all classes)</option>
                {(classes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.levelCode})
                  </option>
                ))}
              </select>
              {!classFilter && (
                <button
                  onClick={openCreate}
                  className="flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  New Lesson
                </button>
              )}
            </div>
          </div>
        </div>

        {classFilter ? (
          <ClassLessonPlanEditor
            classGroupId={classFilter}
            className={selectedClass?.name ?? "this class"}
          />
        ) : (
          <>
            {canReorder && (
              <p className="text-xs text-earth-700 mb-2 inline-flex items-center gap-1">
                <i className="ri-draggable"></i>
                Drag rows to reorder the lessons of this level.
              </p>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-earth-50 border-b border-earth-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Level
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    #
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden sm:table-cell">
                    Month
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden md:table-cell">
                    Files
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-earth-100">
                {orderedLessons.map((lesson, index) => (
                  <tr
                    key={lesson.id}
                    draggable={canReorder}
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={(e) => {
                      if (canReorder) e.preventDefault();
                    }}
                    onDrop={() => {
                      if (canReorder) handleDrop(index);
                    }}
                    className={`hover:bg-earth-50/50 ${canReorder ? "cursor-move" : ""} ${
                      dragIndex === index ? "opacity-40" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-semibold">
                        {lesson.levelCode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-earth-600">
                      <span className="inline-flex items-center gap-1.5">
                        {canReorder && (
                          <i className="ri-draggable text-earth-400"></i>
                        )}
                        {lesson.order === 0 ? "Intro" : lesson.order}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-earth-800 font-medium">
                      {lesson.topic}
                    </td>
                    <td className="px-4 py-3 text-earth-600 hidden sm:table-cell">
                      {MONTHS[lesson.monthAvailable - 1] ??
                        lesson.monthAvailable}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {lesson.filesUrl ? (
                        <i className="ri-check-line text-green-600"></i>
                      ) : (
                        <i className="ri-subtract-line text-earth-300"></i>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          lesson.status === "published"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {lesson.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(lesson)}
                          className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-earth-500"></i>
                        </button>
                        <button
                          onClick={() => setToDelete(lesson)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
              {orderedLessons.length === 0 && (
                <div className="text-center py-12 text-earth-700">
                  No lessons found. Create one to get started.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={closeModal}></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Lesson" : "New Lesson"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Level
                  </label>
                  <select
                    value={form.levelCode}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, levelCode: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  >
                    <option value="">Select</option>
                    {levels?.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.order}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, order: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  />
                  <p className="text-[11px] text-earth-700 mt-1">0 = Intro</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Month
                  </label>
                  <select
                    value={form.monthAvailable}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        monthAvailable: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  >
                    {MONTHS.map((name, i) => (
                      <option key={i + 1} value={i + 1}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Topic
                </label>
                <input
                  type="text"
                  value={form.topic}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, topic: e.target.value }))
                  }
                  placeholder="Lesson topic"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={3}
                  placeholder="What students will learn in this lesson..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Files link (Google Drive)
                </label>
                <input
                  type="url"
                  value={form.filesUrl ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, filesUrl: e.target.value }))
                  }
                  placeholder="https://drive.google.com/..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      status: e.target.value as "draft" | "published",
                    }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createLesson.isPending || updateLesson.isPending}
                  className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createLesson.isPending || updateLesson.isPending
                    ? "Saving..."
                    : isEditing
                      ? "Save Changes"
                      : "Create Lesson"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setToDelete(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-earth-800 mb-2">
              Delete Lesson?
            </h3>
            <p className="text-sm text-earth-700 mb-6">
              <strong>
                {toDelete.levelCode} - {toDelete.topic}
              </strong>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                disabled={deleteLesson.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleteLesson.isPending ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setToDelete(null)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DraftRow {
  key: string;
  lesson: string;
  topic: string;
  status: "draft" | "published";
  monthAvailable: number;
}

/**
 * Editor for a single class group's lesson plan.
 *
 * - Not customized → shows the level's lessons read-only + a "Customize" button.
 * - Customized → an editable DRAFT: drag to reorder, change month, swap lesson
 *   (only when there's a free lesson to swap to), remove, add. Nothing is
 *   persisted until "Save changes" — and it only ever touches this class.
 */
function ClassLessonPlanEditor({
  classGroupId,
  className,
}: {
  classGroupId: string;
  className: string;
}) {
  const { data: plan, isLoading } = useClassLessonPlan(classGroupId);
  const customize = useCustomizeClassLessonPlan();
  const reset = useResetClassLessonPlan();
  const savePlan = useSetClassLessonPlan();

  const [rows, setRows] = useState<DraftRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [addLessonId, setAddLessonId] = useState("");
  const [addMonth, setAddMonth] = useState(1);

  // Reload the draft from the server plan (on first load and after save/reset).
  useEffect(() => {
    setRows(
      (plan?.items ?? []).map((it) => ({
        key: it.id ?? it.lesson,
        lesson: it.lesson,
        topic: it.lessonTopic,
        status: it.lessonStatus,
        monthAvailable: it.monthAvailable,
      })),
    );
    setDirty(false);
    setAddLessonId("");
    setAddMonth(1);
  }, [plan]);

  // Catalog of every lesson in this level (in-plan + pool), for swap/add.
  const catalog = useMemo(() => {
    const map = new Map<string, { id: string; topic: string; status: "draft" | "published" }>();
    (plan?.items ?? []).forEach((it) =>
      map.set(it.lesson, { id: it.lesson, topic: it.lessonTopic, status: it.lessonStatus }),
    );
    (plan?.availablePool ?? []).forEach((l) =>
      map.set(l.id, { id: l.id, topic: l.topic, status: l.status }),
    );
    return map;
  }, [plan]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-earth-200 py-12 flex justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!plan) return null;

  const isCustomized = plan.isCustomized;

  const usedIds = new Set(rows.map((r) => r.lesson));
  // Lessons of this level not currently used in the draft (available to add/swap in).
  const unused = [...catalog.values()].filter((l) => !usedIds.has(l.id));

  const handleDrop = (toIndex: number) => {
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setRows(next);
    setDragIndex(null);
    setDirty(true);
  };

  const swapLesson = (index: number, lessonId: string) => {
    const target = catalog.get(lessonId);
    if (!target) return;
    setRows((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, lesson: target.id, topic: target.topic, status: target.status } : r,
      ),
    );
    setDirty(true);
  };

  const changeMonth = (index: number, month: number) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, monthAvailable: month } : r)));
    setDirty(true);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleAdd = () => {
    const target = catalog.get(addLessonId);
    if (!target) return;
    setRows((prev) => [
      ...prev,
      { key: target.id, lesson: target.id, topic: target.topic, status: target.status, monthAvailable: addMonth },
    ]);
    setAddLessonId("");
    setAddMonth(1);
    setDirty(true);
  };

  const handleSave = () => {
    savePlan.mutate({
      classGroupId,
      items: rows.map((r, i) => ({ lesson: r.lesson, order: i, monthAvailable: r.monthAvailable })),
    });
  };

  return (
    <div className="space-y-4">
      {/* Banner */}
      <div
        className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
          isCustomized ? "bg-olive-50 border-olive-200" : "bg-earth-50 border-earth-200"
        }`}
      >
        <div className="text-sm">
          {isCustomized ? (
            <p className="text-olive-800">
              <i className="ri-git-branch-line mr-1"></i>
              Custom plan for <strong>{className}</strong> — changes here only
              affect this class, never the rest of the level.
            </p>
          ) : (
            <p className="text-earth-600">
              <strong>{className}</strong> follows the level default. Customize
              to reorder, swap, remove or add lessons for this class only.
            </p>
          )}
        </div>
        {isCustomized ? (
          <button
            onClick={() => reset.mutate(classGroupId)}
            disabled={reset.isPending}
            className="px-3 py-2 border border-earth-300 text-earth-700 hover:bg-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-arrow-go-back-line mr-1"></i>
            Reset to level default
          </button>
        ) : (
          <button
            onClick={() => customize.mutate(classGroupId)}
            disabled={customize.isPending}
            className="px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-magic-line mr-1"></i>
            {customize.isPending ? "Customizing..." : "Customize for this class"}
          </button>
        )}
      </div>

      {/* Plan table */}
      <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-earth-50 border-b border-earth-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                  #
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                  Lesson
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                  Month
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden sm:table-cell">
                  Status
                </th>
                {isCustomized && (
                  <th className="text-right px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-earth-100">
              {rows.map((row, index) => (
                <tr
                  key={row.key}
                  draggable={isCustomized}
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => {
                    if (isCustomized) e.preventDefault();
                  }}
                  onDrop={() => {
                    if (isCustomized) handleDrop(index);
                  }}
                  className={`hover:bg-earth-50/50 ${isCustomized ? "cursor-move" : ""} ${
                    dragIndex === index ? "opacity-40" : ""
                  }`}
                >
                  <td className="px-4 py-3 text-earth-600">
                    <span className="inline-flex items-center gap-1.5">
                      {isCustomized && <i className="ri-draggable text-earth-400"></i>}
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-earth-800 font-medium">
                    {/* Only offer a dropdown when there's actually another
                        lesson to swap to; otherwise just show the title. */}
                    {isCustomized && unused.length > 0 ? (
                      <select
                        value={row.lesson}
                        onChange={(e) => swapLesson(index, e.target.value)}
                        className="px-2 py-1 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 max-w-[260px]"
                      >
                        <option value={row.lesson}>{row.topic}</option>
                        {unused.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.topic}
                          </option>
                        ))}
                      </select>
                    ) : (
                      row.topic
                    )}
                  </td>
                  <td className="px-4 py-3 text-earth-600">
                    {isCustomized ? (
                      <select
                        value={row.monthAvailable}
                        onChange={(e) => changeMonth(index, Number(e.target.value))}
                        className="px-2 py-1 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                      >
                        {MONTHS.map((name, i) => (
                          <option key={i + 1} value={i + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      (MONTHS[row.monthAvailable - 1] ?? row.monthAvailable)
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        row.status === "published"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  {isCustomized && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => removeRow(index)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove from this class"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="text-center py-12 text-earth-700">
            {isCustomized
              ? "No lessons in this class's plan. Add one below or reset to the level default."
              : "This level has no lessons yet."}
          </div>
        )}
      </div>

      {/* Add lesson (customized only, when there's a free lesson) */}
      {isCustomized && unused.length > 0 && (
        <div className="bg-white rounded-xl border border-earth-200 p-4 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-earth-700 mb-1">
              Add a lesson from this level
            </label>
            <select
              value={addLessonId}
              onChange={(e) => setAddLessonId(e.target.value)}
              className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">Select a lesson…</option>
              {unused.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.topic}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-earth-700 mb-1">Month</label>
            <select
              value={addMonth}
              onChange={(e) => setAddMonth(Number(e.target.value))}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              {MONTHS.map((name, i) => (
                <option key={i + 1} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAdd}
            disabled={!addLessonId}
            className="px-4 py-2 border border-olive-300 text-olive-700 hover:bg-olive-50 rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-add-line mr-1"></i>
            Add to draft
          </button>
        </div>
      )}

      {/* Save bar (customized only) */}
      {isCustomized && (
        <div className="flex items-center justify-end gap-3">
          <span className="text-sm text-earth-700">
            {dirty ? (
              <>
                <i className="ri-error-warning-line text-amber-500 mr-1"></i>
                Unsaved changes for <strong>{className}</strong>
              </>
            ) : (
              <>
                <i className="ri-check-line text-green-600 mr-1"></i>
                All changes saved
              </>
            )}
          </span>
          <button
            onClick={handleSave}
            disabled={!dirty || savePlan.isPending}
            className="px-5 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            <i className="ri-save-line mr-1"></i>
            {savePlan.isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminLessonsPage;
